import { IModule, ModuleContent, ModuleParams } from '../types';
import {
  PdfFont,
  PdfBrush,
  PdfSize,
  ETextAlign,
  PdfRect,
  PageGen,
  PdfColor,
  PdfPen,
  PdfPoint,
} from '../../pdf/pdf';

interface MindmapNode {
  text: string;
  children?: MindmapNode[];
}

export const mindmapModule: IModule = {
  name: 'mindmap',

  parse(source: string, attrs: Record<string, string>): ModuleContent {
    const root = parseMindmap(source);
    return {
      type: 'mindmap',
      attrs,
      content: JSON.stringify(root),
    };
  },

  measure(content: ModuleContent, params: ModuleParams): PdfSize {
    const width = parseFloat(content.attrs.width) || 300;
    const height = parseFloat(content.attrs.height) || 200;
    return new PdfSize(width, height);
  },

  draw(content: ModuleContent, rect: PdfRect, align: ETextAlign, params: ModuleParams): void {
    const page = params.page!;
    if (!page) {
      throw new Error('PageGen required for mindmap drawing');
    }

    const data: MindmapNode = JSON.parse(content.content || '{"text":"Root"}');

    try {
      drawMindmap(page, rect, data);
    } catch (e) {
      const font = params.font || new PdfFont(PdfFont.FONT_NAME, 10);
      page.drawString(
        `Mindmap error: ${(e as Error).message}`,
        font,
        PdfBrush.Red,
        null,
        rect,
        ETextAlign.CenterLeft
      );
    }
  },
};

function parseMindmap(source: string): MindmapNode {
  const lines = source.trim().split('\n');
  const root: MindmapNode = { text: 'Main Topic' };

  const currentLevel = 0;
  const parentStack: MindmapNode[] = [root];

  for (const line of lines) {
    const trimmed = line.trim();
    if (!trimmed) continue;

    const match = /^(\s*)(.+)$/.exec(trimmed);
    if (!match) continue;

    const indent = match[1].length;
    const text = match[2].replace(/^[-*•]\s*/, '');

    const level = Math.floor(indent / 2);

    while (parentStack.length > level + 1) {
      parentStack.pop();
    }

    const node: MindmapNode = { text };

    if (parentStack.length > 0) {
      const parent = parentStack[parentStack.length - 1];
      if (!parent.children) {
        parent.children = [];
      }
      parent.children.push(node);
    }

    parentStack.push(node);
  }

  return root;
}

function drawMindmap(page: PageGen, rect: PdfRect, root: MindmapNode): void {
  const centerX = rect.left + rect.width / 2;
  const centerY = rect.bottom + rect.height / 2;
  const radius = Math.min(rect.width, rect.height) / 2 - 10;

  drawNode(page, rect, root, centerX, centerY, radius, 0);
}

function drawNode(
  page: PageGen,
  rect: PdfRect,
  node: MindmapNode,
  x: number,
  y: number,
  radius: number,
  depth: number
): void {
  const colors = ['#4A90D9', '#E74C3C', '#2ECC71', '#F39C12', '#9B59B6', '#1ABC9C'];

  const color = PdfColor.fromHex(colors[depth % colors.length]);
  const pen = new PdfPen(color, 2);
  const brush = new PdfBrush(color);

  const nodeRadius = depth === 0 ? radius * 0.25 : radius * 0.15;
  const nodeRect = new PdfRect(x - nodeRadius, y - nodeRadius, nodeRadius * 2, nodeRadius * 2);

  if (depth > 0) {
    page.drawEllipse(pen, null, nodeRect);
  } else {
    page.drawRectangle(pen, brush, nodeRect);
  }

  const fontSize = depth === 0 ? 12 : Math.max(8, 12 - depth * 2);
  const font = new PdfFont(PdfFont.FONT_NAME, fontSize);
  const textRect = new PdfRect(x - nodeRadius, y - fontSize / 2, nodeRadius * 2, fontSize + 2);
  page.drawString(node.text, font, PdfBrush.White, null, textRect, ETextAlign.Center);

  if (node.children && node.children.length > 0) {
    const angleStep = (Math.PI * 2) / node.children.length;
    const startAngle = depth === 0 ? 0 : -Math.PI / 2;
    const childRadius = radius * 0.6;

    for (let i = 0; i < node.children.length; i++) {
      const angle = startAngle + i * angleStep;
      const childX = x + Math.cos(angle) * childRadius;
      const childY = y + Math.sin(angle) * childRadius;

      const linePen = new PdfPen(color, 1);
      page.drawLine(linePen, new PdfPoint(x, y), new PdfPoint(childX, childY));

      drawNode(page, rect, node.children[i], childX, childY, radius * 0.4, depth + 1);
    }
  }
}
